import { jsx as _jsx } from "react/jsx-runtime";
import { createArtis, DeploymentStore, LayoutStore, ProductCatalogStore, SelectionStore, startDeploymentMonitor, } from "@artis/sdk";
import { useEffect, useMemo, useState } from "react";
import { DeploymentSourceContext } from "./deploymentSource.js";
import { GridSelectProducerContext } from "./gridSelectProducer.js";
import { LayoutProducerContext } from "./layoutProducer.js";
import { ProductsProducerContext } from "./productsProducer.js";
import { ArtisProvider } from "./provider.js";
/**
 * App-root provider for the Artis SDK. Mount once near the top of the tree. It bootstraps
 * one `Artis` instance on top of the worker bridge, manages the handshake and restart
 * recovery, and wires the producer seams that back `grid.selection` and `layout`.
 *
 * Everything below it can call `useArtis`, `useOnSelection`, and (inside an `<ArtisWatcher>`)
 * the watcher hooks. While the handshake is in flight, `useArtis()` returns `null` and the
 * watcher hooks no-op.
 *
 * @example
 * ```tsx
 * <ArtisAppProvider enabled={flags.artis} worker={calcsWorkerProxy}>
 *   <App />
 * </ArtisAppProvider>
 * ```
 */
export const ArtisAppProvider = ({ enabled, worker, onWorkerReady, onBeforeBootstrap, onAfterTeardown, deployment, children, }) => {
    const [artis, setArtis] = useState(null);
    const selectionStore = useMemo(() => new SelectionStore(), []);
    const layoutStore = useMemo(() => new LayoutStore(), []);
    const productCatalogStore = useMemo(() => new ProductCatalogStore(), []);
    const deploymentStore = useMemo(() => new DeploymentStore(deployment?.current ?? ""), [deployment?.current]);
    useEffect(() => {
        if (!deployment)
            return;
        return startDeploymentMonitor({ ...deployment, store: deploymentStore });
    }, [deployment, deploymentStore]);
    const layoutPublish = useMemo(() => ({ setPanes: layoutStore.setPanes, setCommands: layoutStore.setCommands }), [layoutStore]);
    useEffect(() => {
        if (!enabled) {
            setArtis(null);
            return;
        }
        let cancelled = false;
        void (async () => {
            await onBeforeBootstrap?.();
            const instance = await createArtis({
                worker,
                onWorkerReady,
                selectionStore,
                layoutStore,
                productCatalogStore,
                deploymentStore,
            });
            if (!cancelled)
                setArtis(instance);
        })();
        return () => {
            cancelled = true;
            void onAfterTeardown?.();
        };
    }, [
        enabled,
        worker,
        onWorkerReady,
        selectionStore,
        layoutStore,
        productCatalogStore,
        deploymentStore,
        onBeforeBootstrap,
        onAfterTeardown,
    ]);
    return (_jsx(DeploymentSourceContext.Provider, { value: deploymentStore, children: _jsx(GridSelectProducerContext.Provider, { value: enabled ? selectionStore.set : undefined, children: _jsx(LayoutProducerContext.Provider, { value: enabled ? layoutPublish : undefined, children: _jsx(ProductsProducerContext.Provider, { value: enabled ? productCatalogStore.apply : undefined, children: _jsx(ArtisProvider, { value: artis, children: children }) }) }) }) }));
};
//# sourceMappingURL=ArtisAppProvider.js.map